import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "@/components/Motion";
import { ASSETS } from "@/data/content";
import { Button } from "@/components/ui/button";

// The rhino strides in from the right; the hero content splashes in alongside it.
const WALK_DURATION = 1.5;

const splash = (i) => ({
  initial: { opacity: 0, y: 22, scale: 0.96 },
  animate: { opacity: 1, y: 0, scale: 1 },
  transition: { duration: 0.7, delay: 0.2 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
});

const HeroBrand = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yOrb = useTransform(scrollYProgress, [0, 1], [0, 220]);

  return (
    <section
      id="hero-brand"
      ref={ref}
      data-testid="hero-brand"
      className="relative min-h-screen flex items-center noise overflow-hidden"
    >
      <div className="absolute inset-0 grid-lines opacity-60" />
      <motion.div
        style={{ y: yOrb }}
        className="absolute -top-40 -right-40 h-[520px] w-[520px] rounded-full blur-[120px] opacity-50"
        aria-hidden
      >
        <div className="h-full w-full rounded-full bg-[#4d4479]" />
      </motion.div>
      <motion.div
        style={{ y: yOrb }}
        className="absolute bottom-0 left-1/4 h-[360px] w-[360px] rounded-full blur-[130px] opacity-25"
        aria-hidden
      >
        <div className="h-full w-full rounded-full bg-[#16fcfb]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full pt-24">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
          {/* Left: text splashes in */}
          <div className="relative">
            {/* splash ring burst */}
            <motion.div
              aria-hidden
              className="pointer-events-none absolute -left-10 top-1/4 h-64 w-64 rounded-full border border-[#16fcfb]"
              initial={{ scale: 0, opacity: 0.5 }}
              animate={{ scale: 3.2, opacity: 0 }}
              transition={{ duration: 1.1, delay: 0.3, ease: "easeOut" }}
            />

            <motion.div {...splash(0)} className="flex items-center gap-3 mb-7">
              <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-[#16fcfb]">
                An Initiative by CyberFrat
              </span>
            </motion.div>

            <motion.h1
              {...splash(1)}
              className="font-display font-900 tracking-tight leading-[0.95] text-[#97b8ff] whitespace-nowrap text-glow-cyan"
              style={{ fontSize: "clamp(2.5rem, 5.4vw, 4.5rem)" }}
            >
              hackTonomy
            </motion.h1>

            <motion.p
              {...splash(2)}
              className="mt-7 font-body text-lg md:text-xl text-[#97b8ff]/90 max-w-md leading-relaxed"
            >
             India's Own Cybersecurity Certification Body
            </motion.p>

            <motion.div {...splash(3)} className="mt-9 flex flex-wrap gap-4">
              <Button
                data-testid="hero-explore-hcasp-btn"
                onClick={() => scrollToId("hcasp-hero")}
                className="rounded-full h-12 px-7 bg-[#16fcfb] text-[#07070c] hover:bg-[#97b8ff] font-mono2 text-xs uppercase tracking-widest glow-cyan"
              >
                Explore HCASP <ArrowDownRight size={16} className="ml-1" />
              </Button>
              <Button
                data-testid="hero-about-btn"
                onClick={() => scrollToId("about-brand")}
                variant="outline"
                className="rounded-full h-12 px-7 border-[#4d4479] bg-transparent text-white hover:bg-[#404452] font-mono2 text-xs uppercase tracking-widest"
              >
                About Us
              </Button>
            </motion.div>
          </div>

          {/* Right: rhino strides in from off-screen right, moving left */}
          <motion.div
            data-testid="hero-rhino"
            className="relative flex justify-center lg:justify-end"
            initial={{ x: "85vw", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: WALK_DURATION, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-[320px] w-[320px] rounded-full bg-[#16fcfb]/10 blur-3xl" />
            </div>
            {/* walking gait: vertical bob + slight pitch, like footsteps */}
            <motion.img
              src={ASSETS.rhino}
              alt="Hacktonomy robotic rhino"
              className="relative w-[80%] md:w-[74%] lg:w-[92%] object-contain"
              style={{ filter: "drop-shadow(0 24px 60px rgba(22,252,251,0.28))" }}
              animate={{ y: [0, -6, 0], rotate: [0, -0.6, 0, 0.6, 0] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#3d414f]"
      >
        Scroll to begin
      </motion.div>
    </section>
  );
};

export default HeroBrand;
